'use client'
import { faLock } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { zodResolver } from '@hookform/resolvers/zod'
import Image from 'next/image'
import Link from 'next/link'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { SOCIAL_LOGIN_URL } from '@/constants/auth'
import { useGuestLogin } from '@/hooks/business/auth/useGuestLogin'
import { useLogin } from '@/hooks/business/auth/useLogin'
import { EmailFormSchema, type ISignInForm, SignInFormSchema } from '@/schemas/auth/authFormSchemas'
import Button from '~/components/Form/Button'
import Input from '~/components/Form/Input'

export interface TempUserLoginProps {
    onClose: () => void
}

/**
 * E-mail do carrinho para quem não está logado: e-mail sem conta abre a sessão temporária (`custom-login`) e vai para
 * o checkout; e-mail com conta pede a senha e entra pelo login, também para o checkout.
 */
const TempUserLogin: React.FC<TempUserLoginProps> = ({ onClose }) => {
    const [isUserRegistered, setIsUserRegistered] = useState(false)
    const { submitEmail } = useGuestLogin()
    const { login } = useLogin()

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<ISignInForm>({
        // Primeiro só o e-mail; com conta, e-mail e senha.
        resolver: zodResolver(isUserRegistered ? SignInFormSchema : (EmailFormSchema as typeof SignInFormSchema)),
        defaultValues: { email: '', password: '' },
    })

    // Os avisos (sucesso, conta existente, credencial errada, falha) saem nos hooks e nos services.
    const onSubmit = async (data: ISignInForm) => {
        try {
            if (isUserRegistered) {
                await login({ email: data.email, password: data.password }, '/checkout')
            } else if ((await submitEmail(data.email)) === 'registered') {
                setIsUserRegistered(true)
            }
        } catch {
            // erro já avisado
        }
    }

    return (
        <div className="flex flex-col justify-center gap-4">
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-8">
                <Input
                    labelText="Preencha seu email para continuar."
                    register={register('email')}
                    error={errors.email?.message}
                />
                {isUserRegistered && (
                    <div>
                        <Input
                            labelText="Senha"
                            type="password"
                            register={register('password')}
                            error={errors.password?.message}
                        />
                        <Link className="pl-2" href="/esqueci-minha-senha">
                            <FontAwesomeIcon icon={faLock} />
                            <ins className="ml-2">Esqueci a senha!</ins>
                        </Link>
                    </div>
                )}
                <div className="flex items-center justify-center gap-2 max-[380px]:flex-col-reverse">
                    <Button
                        type="button"
                        onClick={onClose}
                        size="md"
                        disabled={isSubmitting}
                        className="text-[16px] text-white cursor-pointer w-[200px] bg-secondary/50 hover:bg-secondary max-md:w-full max-md:mb-2 px-5 pt-2 pb-2 md:mx-2"
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        disabled={isSubmitting}
                        color="tertiary"
                        size="md"
                        className="text-[16px] hover:bg-primary-green bg-tertiary-green text-white w-[200px] px-5 max-md:w-full pt-2 pb-2 max-md:mb-2"
                    >
                        {isUserRegistered ? 'Entrar' : 'Continuar'}
                    </Button>
                </div>
            </form>

            {!isUserRegistered && (
                <>
                    <div className="text-center border-b border-submenu-gray relative flex justify-center items-center">
                        <p className="bg-white px-4 relative -bottom-[13px]">ou</p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <p className="text-center">Faça login utilizando sua rede social</p>
                        <Link href={SOCIAL_LOGIN_URL} className="flex items-center justify-center">
                            <Button size="lg" color="white">
                                <Image
                                    src="/assets/icons/google.svg"
                                    height={24}
                                    width={24}
                                    alt="icone do Gooogle"
                                    className="mr-3"
                                    id="searchBtn"
                                />
                                GOOGLE
                            </Button>
                        </Link>
                    </div>
                </>
            )}
        </div>
    )
}

export default TempUserLogin
